import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { task, waitForProperty, waitForQueue } from 'ember-concurrency';
import WieSettings from 'eflex/constants/work-instructions/wie-settings';
import { IMPORT_TYPES_ALLOWED_AS_WI } from 'eflex/constants/file-types';
import {
  EditorModes, EflexObjTypes, ObjectTypesToEditorModes, ShapeTypes,
} from 'eflex/constants/work-instructions/tool-props';
import { decOpacityToHex } from 'eflex/util/opacity-helper';
import { generateStrokeDashArray } from 'eflex/util/stroke-helper';
import { getCanvasCoordsForClick, removeSelectedText, pasteTextAtLocation } from 'eflex/util/fabric-helpers';
import moment from 'moment-timezone';
import $ from 'jquery';
import { keyResponder, onKey } from 'ember-keyboard';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { compact } from 'ramda-adjunct';
import ConfigurationHistoryTypes from 'eflex/constants/configuration-history-types';

const INVALID_PASTE = 'INVALID_PASTE';
const TEST_ELEMENT = 'TEST_ELEMENT';
const CLIPBOARD_CANVAS_IDENTIFIER = 'CANVAS';
const CROP_VISIBLE_TYPES = new Set(['image', EflexObjTypes.CROPBOX]);
const TEXT_EVENTS = new Set(['TEXTAREA', 'INPUT']);
const DESELECT_SKIP_TARGETS = new Set(['div', 'h4', 'label']);

class ToolsDisabled {
  @tracked arrange = true;
  @tracked group = false;
}

@keyResponder
class WorkInstructionsWorkInstructionController extends Controller {
  @service notifier;
  @service imageEditor;
  @service store;
  @service workInstructionRepo;
  @service workInstructionImageRepo;
  @service systemConfig;
  @service eventBus;
  @service router;
  @service validationErrorNotifier;
  @service intl;
  @service currentUser;

  @controller('workInstructions') WorkInstructionsController;
  @controller('workInstructions.index') WorkInstructionsIndexController;

  @tracked workInstruction;
  @tracked autoVersion;
  @tracked currentLibraryView = WieSettings.libraryViews.THUMB;
  @tracked currentDrawingMode = EditorModes.NORMAL;
  @tracked workInstructionToSave;
  @tracked toolsDisabled = new ToolsDisabled();
  @tracked selectedType;
  @tracked enableGrid = false;
  @tracked showSaveModal = false;
  @tracked showChangeNotificationModal = false;
  @tracked showConfigurationHistoryNotesModal = false;
  @tracked canCopy = false;
  @tracked showContextMenu = false;
  @tracked contextMenuX = 0;
  @tracked contextMenuY = 0;
  @tracked pasteX = 0;
  @tracked pasteY = 0;
  @tracked currentDrawerTab = 'tool-properties';
  @tracked selectedShape = ShapeTypes.RECT;
  @tracked locked = false;

  _addedListeners = false;

  editorModes = EditorModes;
  supportedFileTypes = IMPORT_TYPES_ALLOWED_AS_WI;
  controlKey = /Mac/.test(navigator.userAgent) ? 'cmd' : 'ctrl';
  wieConfigurationHistoryType = ConfigurationHistoryTypes.WIE;

  get isInvalid() {
    return this.workInstructionToSave?.isInvalid ?? false;
  }

  get isDirty() {
    return this.workInstructionToSave?.isDirty ?? false;
  }

  get canEdit() {
    return this.workInstructionRepo.userCanEdit(this.workInstruction);
  }

  get canCreate() {
    return this.workInstructionRepo.userCanCreate(this.WorkInstructionsIndexController.selectedFolder);
  }

  get canCreateFolder() {
    return this.workInstructionRepo.userHasRootFolderEditor();
  }

  get writableFolders() {
    return this.workInstructionRepo.getWritableFolders();
  }

  get defaultFolder() {
    if (this.canCreateFolder) {
      return this.intl.t('none');
    } else {
      return null;
    }
  }

  get contextMenuTop() {
    if (!this.showContextMenu) {
      return undefined;
    }

    return `${this.contextMenuY}px`;
  }

  get contextMenuLeft() {
    if (!this.showContextMenu) {
      return undefined;
    }

    return `${this.contextMenuX}px`;
  }

  get visibleMode() {
    if (this.selectedType == null) {
      return this.currentDrawingMode;
    }

    if (this.currentDrawingMode === EditorModes.CROP && CROP_VISIBLE_TYPES.has(this.selectedType)) {
      return EditorModes.CROP;
    }

    const mode = Object.keys(ObjectTypesToEditorModes).find(editorMode =>
      ObjectTypesToEditorModes[editorMode].has(this.selectedType),
    ) ?? EditorModes.NORMAL;

    if (mode === EditorModes.SELECTION && isEmpty(this.imageEditor.canvas.getActiveObjects())) {
      return EditorModes.NORMAL;
    }

    return mode;
  }

  get canvasObjectSelected() {
    return Boolean(this.selectedType);
  }

  initEditor = task(waitFor(async () => {
    Object.assign(this, {
      keyboardActivated: true,
      workInstructionToSave: this.workInstruction,
    });

    this.WorkInstructionsIndexController.selectedFolder = this.workInstruction.folder;
    await waitForQueue('afterRender');

    /* eslint-disable-next-line ember/no-jquery */
    $(globalThis)
      .on('copy.windowCopy', (event) => {
        this._handleCopy.perform(event);
      })
      .on('cut.windowCut', (event) => {
        this._handleCut.perform(event);
      })
      .on('paste.windowPaste', (event) => {
        this._handlePaste.perform(event);
      });

    const options = {
      width: this.workInstruction.width,
      height: this.workInstruction.height,
    };

    if (this.workInstruction?.isNew) {
      const config = this.systemConfig.getWieDefaults('normal');

      options.backgroundColor = config.background;
    }

    await this.imageEditor.initialize('work-instruction-canvas', options);

    this.imageEditor
      .on('selection:cleared', this.#selectionCleared)
      .on('wie:history:modified', this._wieHistoryModified.perform)
      .on('wie:canvas:loaded', this.#wieCanvasLoaded)
      .on('wie:crop:applied', this.#wieCropApplied)
      .on('object:added', this.#objectChange)
      .on('object:removed', this.#objectChange)
      .on('selection:created', this.#selectionChange)
      .on('selection:updated', this.#selectionChange);

    this._addedListeners = true;

    await this.loadCanvasFromModel.perform();
    this.#disableGrid();
  }));

  loadCanvasFromModel = task(waitFor(async () => {
    const savedCanvas = this.workInstruction?.canvas;
    if (savedCanvas != null) {
      await this.imageEditor.loadCanvasJson.perform(savedCanvas);
    } else {
      this.imageEditor.enableHistory();
    }
  }));

  @action
  onDeleteWorkInstructions(currentWorkInstructionIsDeleted) {
    if (!currentWorkInstructionIsDeleted) {
      return;
    }

    this.workInstruction = null;
    this.workInstructionToSave = null;
    this.router.transitionTo('workInstructions');
  }

  export = task(waitFor(async (downloads, fileType) => {
    const queue = Array.isArray(downloads) ? downloads : [downloads];
    await this.workInstructionRepo.downloadInstructions.perform(queue, fileType);
  }));

  _addImage = task(waitFor(async file => {
    const config = this.systemConfig.getWieDefaults('image');

    const options = {
      opacity: config.opacity / 100,
      stroke: config.stroke?.color + decOpacityToHex(config.stroke?.opacity),
      strokeDashArray: generateStrokeDashArray(config.stroke?.style, config.stroke?.width),
      strokeWidth: config.stroke?.width,
      eflex: {
        fileName: file.name,
        strokeDashArrayStyle: config.stroke?.style,
      },
    };

    const { blobToDataURL } = await import('blob-util');
    const dataUrl = await blobToDataURL(file);
    await this.imageEditor.addImage.perform(dataUrl, options);
  }));

  #disableGrid() {
    this.imageEditor.disableGrid();
    this.enableGrid = false;
  }

  toggleGrid = task(waitFor(async val => {
    this.enableGrid = val;
    await this.imageEditor.toggleGrid.perform();
  }));

  saveCanvasAs = task(waitFor(async () => {
    if (this.workInstructionToSave?.folder != null) {
      this.WorkInstructionsIndexController.selectedFolder = this.workInstructionToSave.folder;
    }

    if (this.workInstruction?.isDirty && !this.workInstruction?.isNew) {
      this.workInstruction.rollbackAttributes();
    }

    await this.save.perform(true, {
      deployedName: null,
      deployedCanvas: null,
      deployedImage: null,
      deployedImageThumb: null,
    });
  }));

  save = task({ drop: true }, waitFor(async (fromSaveAs = false, additionalProperties = {}) => {
    this.setEditorMode(EditorModes.NORMAL, true);

    if (this.workInstructionToSave.name) {
      const dupeName = await this.store.queryRecord('work-instruction', {
        ids: [this.workInstructionToSave.id],
        name: this.workInstructionToSave.name,
      });

      if (dupeName) {
        this.notifier.sendError('workInstruction.duplicateName', { name: this.workInstructionToSave.name });
        return;
      }
    } else {
      await this.WorkInstructionsIndexController.generateUntitledName(this.workInstructionToSave);
    }

    await this.imageEditor.saveCanvasImages.perform(fromSaveAs);

    this.#disableGrid();

    Object.assign(this.workInstructionToSave, {
      autoVersion: this.autoVersion ?? moment().format('YYYYMMDDHHmmss'),
      canvas: await this.imageEditor.getCanvasJson.perform(true),
      width: this.imageEditor.getCanvasWidth(),
      height: this.imageEditor.getCanvasHeight(),
      displayImageData: this.imageEditor.getCanvasImageDataUrl(),
      approvalRequested: false,
      approvalsRejects: [],
      ...additionalProperties,
    });

    if (this.isInvalid) {
      this.validationErrorNotifier.sendErrors(this.workInstructionToSave);
      this.showSaveModal = false;
      return;
    }

    await this.workInstructionRepo.saveWithImageData.perform(this.workInstructionToSave);

    Object.assign(this, {
      workInstruction: this.workInstructionToSave,
      autoVersion: null,
    });

    this.showSaveModal = false;

    if (this.workInstruction.hasAssignedLocations && this.systemConfig.jem?.changeNotifications) {
      this.showChangeNotificationModal = true;
      await waitForProperty(this, 'showChangeNotificationModal', false);
    }

    if (
      this.workInstruction.hasAssignedLocations &&
      this.systemConfig.jem?.configurationHistory
    ) {
      this.showConfigurationHistoryNotesModal = true;
      await waitForProperty(this, 'showConfigurationHistoryNotesModal', false);
    }

    if (!this.workInstruction?.isNew && fromSaveAs) {
      this.loadWorkInstruction(this.workInstruction);
    }
  }));

  saveDefault = task(waitFor(async () => {
    await this.systemConfig.config.save();
    this.notifier.sendSuccess('imageEditor.makeDefaultSaved');
  }));

  @action
  rollback() {
    if (!this.workInstruction?.isDirty || this.workInstruction?.isDestroyed) {
      return;
    }

    this.workInstruction.rollbackAttributes();

    if (this.workInstruction.isDeleted) {
      if (this.workInstruction === this.workInstructionToSave) {
        this.workInstructionToSave = null;
      }
      this.workInstruction = null;
    }
  }

  @action
  duplicateInstruction() {
    this.setEditorMode(EditorModes.NORMAL, true);

    if (!this.workInstruction?.isNew) {
      this.workInstructionToSave = this.workInstruction.copy(true);
      this.workInstructionToSave.folder = this.WorkInstructionsIndexController.selectedFolder;

      const appendedString = `-${this.intl.t('copy').toLowerCase()}`;
      const name = this.workInstructionToSave.name;
      if (name.endsWith(appendedString)) {
        this.workInstructionToSave.name = `${name}2`;
      } else if (new RegExp(`${appendedString}\\d+$`).test(name)) {
        const baseName = name.replace(new RegExp(`${appendedString}\\d+$`), '');
        const match = name.match(new RegExp(`${appendedString}(\\d+)$`));
        const newNumber = parseInt(match[1], 10) + 1;
        this.workInstructionToSave.name = `${baseName}${appendedString}${newNumber}`;
      } else {
        this.workInstructionToSave.name = `${name}${appendedString}`;
      }
    }

    Object.assign(this, {
      autoVersion: moment().format('YYYYMMDDHHmmss'),
      showSaveModal: true,
    });
  }

  // NOTE: Any browser copy or cut event needs to set the flag
  // so a proper determination can be made when pasting. With image
  // uploads the initial object added is of input but type checkbox
  // we still want to copy and paste so its not treated same as
  // the input or textarea
  _copyOrCut = task(waitFor(async (event, isCutting = false) => {
    let breakExecution = false;
    const activeObject = this.imageEditor.canvas?.getActiveObject();

    if (TEXT_EVENTS.has(event?.target.tagName) && event?.target.type !== 'checkbox') {
      this.imageEditor.clearClipboard();
      breakExecution = true;
    } else if (activeObject?.type === 'textbox' && activeObject?.isEditing) {
      const text = activeObject.getSelectedText();

      if (event != null) {
        event.originalEvent.clipboardData.setData('text', text);
        event.preventDefault();
      } else {
        await navigator.clipboard.writeText(text);

        if (isCutting) {
          removeSelectedText(activeObject);
          this.imageEditor.canvas.renderAll();
        }

        breakExecution = true;
      }
    }

    return breakExecution;
  }));

  _handleCopy = task(waitFor(async event => {
    if (await this._copyOrCut.perform(event)) {
      return;
    }

    if (event?.target.tagName !== TEST_ELEMENT) {
      await this.imageEditor.copy.perform();
    }
  }));

  _handleCut = task(waitFor(async event => {
    if (await this._copyOrCut.perform(event, true)) {
      return;
    }

    if (event?.target.tagName !== TEST_ELEMENT) {
      await this.imageEditor.cut.perform();
    }
  }));

  _handlePaste = task(waitFor(async (event, x = null, y = null) => {
    const activeObject = this.imageEditor.canvas?.getActiveObject();

    if (event != null) {
      return await this._handlePasteEvent.perform(event);
    } else if (activeObject?.type === 'textbox' && activeObject?.isEditing) {
      const clipboardText = await navigator.clipboard.readText();
      if (clipboardText?.length < 1) {
        return;
      }
      pasteTextAtLocation(activeObject, clipboardText, activeObject.selectionStart);
      this.imageEditor.canvas.renderAll();
    } else {
      await this.imageEditor.paste.perform(x, y);
    }
  }));

  _handlePasteEvent = task(waitFor(async event => {
    if (TEXT_EVENTS.has(event.target.tagName) && event.target.type !== 'checkbox') {
      return INVALID_PASTE;
    }

    event.preventDefault();
    event.stopPropagation();

    const pastedItem = [...event.originalEvent.clipboardData.items].find(
      (item) => item.type.includes('image') || item.type === 'text/plain',
    );

    if (pastedItem == null) {
      return;
    }

    if (pastedItem.type === 'text/plain') {
      const pastedString = await new Promise((resolve) => { pastedItem.getAsString(resolve); });

      if (pastedString !== CLIPBOARD_CANVAS_IDENTIFIER) {
        return;
      }

      await this.imageEditor.paste.perform();
    } else {
      const file = pastedItem.getAsFile();

      if (!this.workInstructionImageRepo.fileAllowed(file)) {
        return;
      }

      await this._addImage.perform(file);
    }
  }));

  undo = task(waitFor(async () => {
    await this.imageEditor.undo.perform();
    this.setEditorMode(EditorModes.NORMAL, true);
  }));

  redo = task(waitFor(async () => {
    await this.imageEditor.redo.perform();
    this.setEditorMode(EditorModes.NORMAL, true);
  }));

  refreshInstruction = task(waitFor(async () => {
    await this.store.findRecord('work-instruction', this.workInstruction.id, { reload: true });
    await this.loadCanvasFromModel.perform();
  }));

  onAddImage = task(waitFor(async ([file]) => {
    if (file == null || !this.supportedFileTypes.includes(file.type)) {
      this.notifier.sendError('plant.station.jem.wrongImageFormat');
      return;
    }

    if (file.size > WieSettings.maxFileSize) {
      this.notifier.sendError('plant.station.jem.fileTooLarge');
      return;
    }

    await this._addImage.perform(file);
  }));

  copy = task(waitFor(async () => {
    this.showContextMenu = false;
    await this._handleCopy.perform();
  }));

  paste = task(waitFor(async () => {
    this.showContextMenu = false;
    await this._handlePaste.perform(null, this.pasteX, this.pasteY);
  }));

  cut = task(waitFor(async () => {
    this.showContextMenu = false;
    await this._handleCut.perform();
  }));

  onDeleteKey = task({ drop: true }, waitFor(async () => {
    await this.imageEditor.removeSelectedObject.perform();
  }));

  @onKey('Meta+z')
  @onKey('Ctrl+z')
  undoKey() {
    this.undo.perform();
  }

  @onKey('Meta+Shift+z')
  @onKey('Ctrl+Shift+z')
  redoKey() {
    this.redo.perform();
  }

  @onKey('Delete')
  @onKey('Backspace')
  deleteKey() {
    this.onDeleteKey.perform();
  }

  @onKey('ArrowUp')
  upKey() {
    if (this.currentDrawingMode === EditorModes.CROP) {
      return;
    }

    this.imageEditor.moveActiveObject('y', -1);
  }

  @onKey('ArrowDown')
  downKey() {
    if (this.currentDrawingMode === EditorModes.CROP) {
      return;
    }

    this.imageEditor.moveActiveObject('y', 1);
  }

  @onKey('ArrowRight')
  rightKey() {
    if (this.currentDrawingMode === EditorModes.CROP) {
      return;
    }

    this.imageEditor.moveActiveObject('x', 1);
  }

  @onKey('ArrowLeft')
  leftKey() {
    if (this.currentDrawingMode === EditorModes.CROP) {
      return;
    }

    this.imageEditor.moveActiveObject('x', -1);
  }

  @action
  loadWorkInstruction(workInstruction) {
    this.router.transitionTo('workInstructions.workInstruction', workInstruction.id);
  }

  @action
  cancelSave() {
    Object.assign(this, {
      workInstructionToSave: this.workInstruction,
      showSaveModal: false,
    });
  }

  @action
  contextMenu(event) {
    if (event.target.tagName !== 'CANVAS') {
      return;
    }
    event.preventDefault();

    const { x, y } = getCanvasCoordsForClick(this.imageEditor.canvas, event);

    this.imageEditor.activateObjectByPoint(x, y);

    Object.assign(this, {
      canCopy: this.imageEditor.canvas.getActiveObject() != null,
      showContextMenu: true,
      contextMenuX: event.x,
      contextMenuY: event.y,
      pasteX: x,
      pasteY: y,
    });
  }

  @action
  deselectWIEObjects(e) {
    if (this.isLibraryFullScreen) {
      this.showContextMenu = false;
      return;
    }

    const skipTargets = [
      // eslint-disable-next-line ember/no-jquery
      $(e?.target).parents('.property-drawer')?.length,
      // eslint-disable-next-line ember/no-jquery
      $(e?.target).parents('.library-card')?.length,
      // eslint-disable-next-line ember/no-jquery
      $(e?.target).parents('.modal-open').length,
      !DESELECT_SKIP_TARGETS.has(e?.target.tagName?.toLowerCase()),
    ];

    if (!isEmpty(compact(skipTargets))) {
      this.showContextMenu = false;
      return;
    }

    this.showContextMenu = false;
    this.setEditorMode(EditorModes.NORMAL, true);
  }

  @action
  objectCreated(obj) {
    this.setEditorMode(EditorModes.NORMAL, true);
    this.imageEditor.canvas.setActiveObject(obj);
  }

  onLockChange = (bool) => {
    this.locked = bool;
    const disabled = this.imageEditor.cannotArrange() || bool;
    this.toolsDisabled.arrange = disabled;
  };

  @action
  onPanelTabActive(type) {
    this.currentDrawerTab = type;
    if (type === 'tool-properties' || type === 'files') {
      this.currentLibraryView = WieSettings.libraryViews.THUMB;
    }
  }

  // service hooks start
  #selectionCleared = () => {
    this.selectedType = null;
    this.eventBus.trigger('updateSelectedTool');
  };

  #selectionChange = () => {
    const obj = this.imageEditor.canvas.getActiveObject();
    if (obj == null || obj.eflex?.childObject != null) {
      return;
    }

    this.selectedType = obj.eflex?.type ?? obj.type;

    const disabledGroup = obj._objects?.reduce((acc, _obj) => {
      if (_obj.eflex?.dynamicObjectName?.length > 0 || this.imageEditor.isLocked(_obj)) {
        acc++;
      }
      return acc;
    }, 0);

    const disabledArrange = this.imageEditor.cannotArrange() || this.imageEditor.isLocked(obj);

    Object.assign(this.toolsDisabled, {
      arrange: disabledArrange,
      group: disabledGroup,
    });

    if (this.currentDrawingMode === EditorModes.CROP && !CROP_VISIBLE_TYPES.has(this.selectedType)) {
      this.setEditorMode(EditorModes.NORMAL, false);
    }

    this.currentDrawerTab = 'tool-properties';
    this.eventBus.trigger('updateSelectedTool');
    this.imageEditor.canvas.renderAll();
  };

  #objectChange = () => {
    this.toolsDisabled.arrange = this.imageEditor.cannotArrange();
  };

  #wieCanvasLoaded = () => {
    this.toolsDisabled.arrange = this.imageEditor.cannotArrange;
    this.currentDrawerTab = 'tool-properties';
  };

  _wieHistoryModified = task(waitFor(async () => {
    this.workInstruction.canvas = await this.imageEditor.getCanvasJson.perform(true);
  }));

  #wieCropApplied = (image) => {
    this.setEditorMode(EditorModes.NORMAL, true);
    this.imageEditor.canvas.setActiveObject(image);
  };
  // service hooks start

  async cleanup() {
    // eslint-disable-next-line ember/no-jquery
    $(globalThis)
      .off('.windowCopy')
      .off('.windowPaste')
      .off('.windowCut');

    if (this._addedListeners) {
      this.imageEditor
        .off('selection:cleared', this.#selectionCleared)
        .off('wie:history:modified', this._wieHistoryModified.perform)
        .off('wie:canvas:loaded', this.#wieCanvasLoaded)
        .off('wie:crop:applied', this.#wieCropApplied)
        .off('object:added', this.#objectChange)
        .off('object:removed', this.#objectChange)
        .off('selection:created', this.#selectionChange)
        .off('selection:updated', this.#selectionChange);

      this._addedListeners = false;
    }

    this.workInstruction?.rollbackAttributes();

    Object.assign(this, {
      currentLibraryView: WieSettings.libraryViews.THUMB,
      keyboardActivated: false,
      currentDrawingMode: EditorModes.NORMAL,
      selectedType: null,
      enableGrid: false,
      showSaveModal: false,
      autoVersion: null,
      canCopy: false,
      showContextMenu: false,
      contextMenuX: 0,
      contextMenuY: 0,
      pasteX: 0,
      pasteY: 0,
      workInstruction: null,
      workInstructionToSave: null,
      currentDrawerTab: 'tool-properties',
    });

    this.WorkInstructionsIndexController.selectedFolder = null;

    await this.imageEditor.cleanup();
  }

  @action
  setEditorMode(mode, discardActive) {
    this.currentDrawingMode = mode;

    switch (mode) {
      case EditorModes.NORMAL: {
        this.imageEditor.startNormalMode(discardActive);
        break;
      }
      case EditorModes.LINES: {
        this.imageEditor.startFreeDrawingMode();
        break;
      }
      case EditorModes.TEXT: {
        this.imageEditor.startTextMode();
        break;
      }
      case EditorModes.SHAPE:
      case EditorModes.ICONS: {
        this.imageEditor.startIconsOrShapeMode();
        break;
      }
      case EditorModes.CROP: {
        if (this.selectedType !== 'image') {
          this.imageEditor.startNormalMode(discardActive);
        }
        break;
      }
      default: {
        throw new Error('Unknown editor mode.');
      }
    }

    this.currentDrawerTab = 'tool-properties';
  }
}

export default WorkInstructionsWorkInstructionController;
